import type { Clock, IdGenerator } from "../ports/clock";
import type { FoodRepository } from "../ports/repositories";
import type { Food, FoodBasis, FoodMeasure } from "../../domain/foods/food";
import { validateFood } from "../../domain/foods/food";
import type { NutrientProfile } from "../../domain/nutrition/nutrients";
import type { ImportedFoodDraft } from "../../domain/foods/import-draft";
import { confirmImportedFood } from "../../domain/foods/import-draft";

export async function saveCustomFood(
  dependencies: { readonly foods: FoodRepository; readonly clock: Clock; readonly ids: IdGenerator },
  input: {
    readonly name: string;
    readonly brand?: string;
    readonly barcode?: string;
    readonly basis: FoodBasis;
    readonly nutrients: NutrientProfile;
    /** Retain that reviewed nutrition began as a photo estimate. */
    readonly source?: "custom" | "photo-estimate";
  },
): Promise<Food> {
  const now = dependencies.clock.now();
  const food = validateFood({
    id: dependencies.ids.next(),
    name: input.name.trim(),
    brand: input.brand?.trim() || undefined,
    barcode: input.barcode?.trim() || undefined,
    basis: input.basis,
    nutrients: input.nutrients,
    provenance: { kind: input.source ?? "custom" },
    preferred: true,
    createdAt: now,
    updatedAt: now,
  });
  await dependencies.foods.save(food);
  return food;
}

function canRefreshImportedMeasures(food: Food, draft: ImportedFoodDraft): boolean {
  return food.provenance.kind === "imported"
    && (food.measures?.length ?? 0) === 0
    && (draft.candidate.measures?.length ?? 0) > 0
    && food.basis.unit === draft.candidate.basis.unit
    && food.provenance.provider === draft.candidate.provider
    && food.provenance.providerFoodId === draft.candidate.providerFoodId;
}

export async function saveImportedFood(
  dependencies: { readonly foods: FoodRepository; readonly clock: Clock; readonly ids: IdGenerator },
  input: {
    readonly draft: ImportedFoodDraft;
    readonly finalNutrients: NutrientProfile;
    readonly userReviewedMacros: boolean;
    /** A matching legacy import that can be refreshed without changing its canonical unit. */
    readonly existingFood?: Food;
    readonly metadata?: {
      readonly name: string;
      readonly brand?: string;
      readonly barcode?: string;
      readonly basis: FoodBasis;
      readonly measures?: readonly FoodMeasure[];
    };
  },
): Promise<Food> {
  if (input.existingFood && !canRefreshImportedMeasures(input.existingFood, input.draft)) {
    throw new Error("The existing food does not match this provider measure refresh");
  }
  const now = dependencies.clock.now();
  const confirmed = confirmImportedFood({
    id: input.existingFood?.id ?? dependencies.ids.next(),
    draft: input.draft,
    finalNutrients: input.finalNutrients,
    userReviewedMacros: input.userReviewedMacros,
    now,
  });
  const imported = input.existingFood
    ? validateFood({ ...confirmed, createdAt: input.existingFood.createdAt })
    : confirmed;
  const food = input.metadata
    ? validateFood({
        ...imported,
        name: input.metadata.name.trim(),
        brand: input.metadata.brand?.trim() || undefined,
        barcode: input.metadata.barcode?.trim() || undefined,
        basis: input.metadata.basis,
        measures: input.metadata.basis.unit === imported.basis.unit ? input.metadata.measures : undefined,
      })
    : imported;
  await dependencies.foods.save(food);
  return food;
}
